/*
 * Copyright (c) 2024 Dave van Rijn Development
 */

import React, {createContext, CSSProperties, forwardRef, PropsWithChildren, ReactElement, useMemo} from 'react';

import InfiniteLoader from 'react-window-infinite-loader';

import {FixedSizeList} from 'react-window';

import AutoSizer, {Size} from 'react-virtualized-auto-sizer';

type InternalRowProps = {index: number; style: CSSProperties};
export type RenderRowProps = InternalRowProps & {isLoaded: boolean};
export type ListLoader = (startIndex: number) => Promise<void>;

interface Props {
    onLoadMore: ListLoader;
    renderRow: (props: RenderRowProps) => ReactElement;
    hasMore: boolean;
    isLoadingMore: boolean;
    itemCount: number;
    itemSize: number;

    // Sticky props
    renderStickyRow?: (props: RenderRowProps) => ReactElement;
    stickyIndexes?: Array<number>;
}

type StickyContext = {
    stickyIndexes: Array<number>;
    renderItem: (props: RenderRowProps) => ReactElement | null;
};

const StickyListContext = createContext<StickyContext>({
    stickyIndexes: [] as Array<number>,
    renderItem: () => null
});
StickyListContext.displayName = 'StickyListContext';

function StickyItemWrapper(props: RenderRowProps & {data: StickyContext}) {
    const {data, index} = props;
    const {renderItem, stickyIndexes} = data;
    if (stickyIndexes && stickyIndexes.includes(index)) return null;
    return renderItem(props);
}

export default function InfiniteList(props: Props) {
    const {stickyIndexes, renderStickyRow, renderRow, itemSize, hasMore, onLoadMore, isLoadingMore} = props;
    const itemCount = useMemo(() => {
        let count = props.itemCount;
        if (hasMore) count++;
        if (stickyIndexes) count += stickyIndexes.length;
        return count;
    }, [hasMore, props.itemCount, stickyIndexes]);
    const _onLoadMore = isLoadingMore ? () => {} : onLoadMore;
    const innerElementType = forwardRef(({children, ...rest}: PropsWithChildren<any>, ref: any) => (
        <StickyListContext.Consumer>
            {({stickyIndexes}) => (
                <div ref={ref} {...rest}>
                    {stickyIndexes.map(index => {
                        const style: CSSProperties = {
                            top: index * props.itemSize,
                            left: 0,
                            width: '100%',
                            height: props.itemSize,
                            position: 'sticky',
                            zIndex: 1,
                            backgroundColor: 'white'
                        };
                        return _renderStickyRow({style, index});
                    })}
                    {children}
                </div>
            )}
        </StickyListContext.Consumer>
    ));

    function isLoaded(index: number) {
        return !hasMore || index < itemCount - 1;
    }

    function _renderRow(props: InternalRowProps) {
        const index = props.index - (stickyIndexes?.length ?? 0);
        const loaded = isLoaded(index);
        return renderRow({...props, index, isLoaded: loaded});
    }

    function _renderStickyRow(props: InternalRowProps) {
        return renderStickyRow?.({...props, isLoaded: true});
    }

    return (
        <StickyListContext.Provider value={{renderItem: _renderRow, stickyIndexes: props.stickyIndexes ?? []}}>
            <AutoSizer>
                {({height, width}: Size) => (
                    <InfiniteLoader isItemLoaded={isLoaded} itemCount={itemCount} loadMoreItems={_onLoadMore}>
                        {({onItemsRendered, ref}) => (
                            <FixedSizeList
                                width={width}
                                height={height}
                                itemCount={itemCount}
                                ref={ref}
                                innerElementType={innerElementType}
                                itemData={{renderItem: _renderRow, stickyIndexes: stickyIndexes ?? []}}
                                onItemsRendered={onItemsRendered}
                                itemSize={itemSize}
                            >
                                {StickyItemWrapper}
                            </FixedSizeList>
                        )}
                    </InfiniteLoader>
                )}
            </AutoSizer>
        </StickyListContext.Provider>
    );
}
